import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@core/theme/base.css";
import { ThemeProvider } from "@core/theme/ThemeProvider";
import { SessionProvider } from "@core/session/SessionProvider";
import { PortalApp } from "@portal/App";
import { ValidatorApp } from "@validator/App";
import {
  createMutationCache,
  createQueryCache,
} from "@portal/common/queryErrorFallback";

// T2 (2026-09-19, qa-bug-matrix-2026-09.md §6): red de seguridad global.
// 18 llamadas `.mutate(...)` del portal no traían `onError`, y un listado
// que falla no tenía ningún fallback tampoco — ninguno de los dos casos se
// arregla uno por uno. El fallback en sí (con el diferido de mutaciones que
// evita el doble toast) vive en `@portal/common/queryErrorFallback` —queda
// separado de este archivo para poder probarlo sin importar `main.tsx`,
// que monta la app real al importarse—. `notifyError`, que ambas caches
// usan por debajo, es un no-op si `ToastProvider` no está montado (el árbol
// de `ValidatorApp` cuelga de este mismo `QueryClient` pero fuera de
// `ToastProvider`, ver el comentario de las `Routes` más abajo), así que
// este fallback nunca puede romper esa superficie.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  queryCache: createQueryCache(),
  mutationCache: createMutationCache(),
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("No se encontró #root");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* SPEC-301: el validador se autentica con bearer JWT
                (api/contracts/parts/00-base.yaml:509-514), no con la
                cookie HttpOnly + CSRF del portal (ADR-005). Por eso queda
                fuera de `SessionProvider`, que es exclusivamente la sesión
                del portal — montarlo debajo lo metería en el modelo de
                sesión equivocado. */}
            <Route path="/validator/*" element={<ValidatorApp />} />
            <Route
              path="/*"
              element={
                <SessionProvider>
                  <PortalApp />
                </SessionProvider>
              }
            />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
